import Image from 'next/image';
import React, { useState } from 'react';

interface IconDetailsProps {
  src: string; // Image source for the icon
  alt?: string; // Alt text for accessibility
  className?: string; // Optional additional classes
  title: string;
  desc: string;
}

const IconDetails: React.FC<IconDetailsProps> = ({
  src,
  alt = '',
  className = '',
  title,
  desc,
}) => {
  const [imgSrc, setImgSrc] = useState(() => {
    // Validate src on initialization
    if (!src) {
      console.warn('IconDetails: No src provided for title:', title);
      return '/assets/images/No-Image.jpg';
    }
    return src;
  });
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTitleExpanded, setIsTitleExpanded] = useState(false);

  // Define character limit for truncation
  const CHAR_LIMIT = 150;

  const shouldTruncate = desc.length > CHAR_LIMIT;

  const displayText =
    isExpanded || !shouldTruncate
      ? desc
      : `${desc.substring(0, CHAR_LIMIT)}...`;

  return (
    <div className='flex flex-col items-start text-left w-full h-full'>
      <div
        className='bg-gray-100 flex items-center justify-center md:mb-5 mb-3 rounded-lg'
        style={{ width: '36px', height: '36px' }}
      >
        <Image
          src={imgSrc}
          alt={alt}
          width={20}
          height={20}
          className={`icon object-contain ${className}`}
          onError={() => {
            console.warn(
              'IconDetails: Failed to load image:',
              imgSrc,
              'for title:',
              title
            );
            setImgSrc('/assets/images/No-Image.jpg');
          }}
        />
      </div>
      <div className='md:text-md text-sm capitalize md:mb-5 mb-3 font-medium w-full'>
        <div
          className={`leading-tight ${isTitleExpanded ? '' : 'overflow-hidden'}`}
          style={{
            display: isTitleExpanded ? 'block' : '-webkit-box',
            WebkitLineClamp: isTitleExpanded ? 'unset' : 2,
            WebkitBoxOrient: 'vertical',
            lineHeight: '1.2',
          }}
        >
          {title}
        </div>
        {title.length > 50 && (
          <button
            onClick={() => setIsTitleExpanded(!isTitleExpanded)}
            className='text-black font-medium mt-1 text-xs underline'
          >
            {isTitleExpanded ? 'Show Less' : 'Show More'}
          </button>
        )}
      </div>
      <div className='md:text-sm text-xs text-left leading-relaxed flex-1 flex flex-col'>
        <p className='flex-1'>{displayText}</p>
        <div className='mt-auto'>
          {shouldTruncate && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className='text-black font-medium mt-1 text-xs underline'
            >
              {isExpanded ? 'View Less' : 'View More'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default IconDetails;
